import { createElement, useEffect, useRef, useState } from 'react';

type TInfiniteScrollReturn<TRootElement> = {
  rootRef: React.RefObject<TRootElement>;
  sentryElement: React.ReactHTMLElement<HTMLDivElement>;
  updateTrigger: unknown;
};

const useInfiniteScroll = <TRootElement extends HTMLElement = HTMLDivElement>({
  rootMargin = 0,
}: {
  rootMargin?: number;
}): TInfiniteScrollReturn<TRootElement> => {
  const [updateTrigger, setUpdateTrigger] = useState<number>(0);

  // Callback ref held in state so the observer attaches whenever the sentry mounts. Depending on `ref.current` in
  // the effect deps doesn't work, as refs are only set after render, so a sentry that mounts after the first render
  // (e.g. once the first page of data arrives) was never observed.
  const [sentryNode, setSentryNode] = useState<HTMLDivElement | null>(null);
  const rootRef = useRef<TRootElement>(null);

  useEffect(() => {
    if (!sentryNode) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setUpdateTrigger(Math.random());
        }
      },
      { root: rootRef.current, threshold: 0, rootMargin: `0px 0px ${rootMargin}px 0px` }
    );

    observer.observe(sentryNode);

    return () => observer.disconnect();
  }, [sentryNode]);

  const sentryElement = createElement<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>('div', {
    ref: setSentryNode,
  });

  return { rootRef, sentryElement, updateTrigger };
};

export default useInfiniteScroll;
